// SMSF Mobile Wrapper - Responsive container for mobile optimization
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const SMSFMobileWrapper = ({ children, tabs, activeTab, onTabChange }) => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isTablet, setIsTablet] = useState(false);

    // Detect screen size
    useEffect(() => {
        const checkScreenSize = () => {
            const width = window.innerWidth;
            setIsMobile(width < 768);
            setIsTablet(width >= 768 && width < 1024);
        };

        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    // Close mobile menu when tab changes
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [activeTab]);

    // Prevent body scroll when menu is open
    useEffect(() => {
        if (isMobileMenuOpen && isMobile) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMobileMenuOpen, isMobile]);

    const currentTab = tabs.find(tab => tab.id === activeTab);

    return (
        <div className="smsf-mobile-wrapper">
            {/* Mobile Header */}
            {isMobile && (
                <div className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-50">
                    <div className="flex items-center justify-between p-4">
                        <div className="flex items-center">
                            <button
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                className="mr-3 p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                aria-label="Toggle menu"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    {isMobileMenuOpen ? (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    ) : (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                    )}
                                </svg>
                            </button>
                            <div>
                                <div className="font-semibold text-gray-800">SMSF Calculator</div>
                                <div className="text-xs text-gray-600">
                                    {currentTab?.icon} {currentTab?.label}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Mobile Navigation Menu */}
            {isMobile && (
                <>
                    {/* Overlay */}
                    {isMobileMenuOpen && (
                        <div 
                            className="fixed inset-0 bg-black bg-opacity-50 z-40"
                            onClick={() => setIsMobileMenuOpen(false)}
                        />
                    )}
                    
                    {/* Slide-out Menu */}
                    <div className={`fixed top-0 left-0 bottom-0 w-80 bg-white shadow-xl z-50 transform transition-transform duration-300 ${
                        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
                    }`}>
                        <div className="p-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-800">Navigation</h3>
                        </div>
                        <div className="overflow-y-auto h-full pb-24">
                            <nav className="p-2">
                                {tabs.map(tab => (
                                    <button
                                        key={tab.id}
                                        onClick={() => onTabChange(tab.id)}
                                        className={`w-full flex items-center px-4 py-3 mb-1 rounded-lg transition-colors ${
                                            activeTab === tab.id
                                                ? 'bg-blue-600 text-white'
                                                : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                    >
                                        <span className="text-2xl mr-3">{tab.icon}</span>
                                        <span className="text-left font-medium">{tab.label}</span>
                                    </button>
                                ))}
                            </nav>
                        </div>
                    </div>
                </>
            )}

            {/* Tablet Navigation (Horizontal Scroll) */}
            {isTablet && !isMobile && (
                <div className="sticky top-0 bg-white border-b border-gray-200 z-40 overflow-x-auto">
                    <nav className="flex space-x-1 p-2 min-w-max">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => onTabChange(tab.id)}
                                className={`flex flex-col items-center px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                                    activeTab === tab.id
                                        ? 'bg-blue-600 text-white'
                                        : 'text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                                <span className="text-xl mb-1">{tab.icon}</span>
                                <span className="text-xs font-medium">{tab.label}</span>
                            </button>
                        ))}
                    </nav>
                </div>
            )}

            {/* Content Area */}
            <div className={`${isMobile ? 'pt-16' : ''}`}>
                {children}
            </div>

            {/* Mobile Bottom Navigation (Optional) */}
            {isMobile && (
                <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40">
                    <div className="flex justify-around p-2">
                        {tabs.slice(0, 5).map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => onTabChange(tab.id)}
                                className={`flex flex-col items-center px-3 py-2 rounded-lg transition-colors ${
                                    activeTab === tab.id
                                        ? 'text-blue-600'
                                        : 'text-gray-600'
                                }`}
                            >
                                <span className="text-xl">{tab.icon}</span>
                                <span className="text-xs mt-1">{tab.label.split(' ')[0]}</span>
                            </button>
                        ))}
                        <button
                            onClick={() => setIsMobileMenuOpen(true)}
                            className="flex flex-col items-center px-3 py-2 rounded-lg text-gray-600"
                        >
                            <span className="text-xl">⋯</span>
                            <span className="text-xs mt-1">More</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

SMSFMobileWrapper.propTypes = {
    children: PropTypes.node.isRequired,
    tabs: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        icon: PropTypes.string
    })),
    activeTab: PropTypes.string.isRequired,
    onTabChange: PropTypes.func.isRequired
};

export default SMSFMobileWrapper;
